import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { ISelection } from 'old-store/types/gallery';

export type MobileMenuView =
  | 'main'
  | 'albums'
  | 'selections'
  | 'share'
  | 'share-send'
  | 'share-copy'
  | 'selection-actions'
  | 'selection-save'
  | 'selection-download'
  | 'selection-share';

interface IOpenState {
  isOpen: boolean;
}

interface IDownloadModal extends IOpenState {
  // 'filtered' reopens the sheet on the filtered download after it failed behind the PIN modal
  scope: 'default' | 'currentSelection' | 'filtered';
}

interface IDownloadPinModal extends IOpenState {
  action?: 'startDownload' | 'next';
  url?: string;
  next?: (pin?: string) => void;
}

interface ISelectionModal extends IOpenState {
  selectionId?: string;
  selectionName?: string;
}

interface IDeleteCommentModal extends IOpenState {
  commentId: string | null;
  imageId: string | null;
}

// Which button the comments panel belongs to while it is open. `null` means the lightbox flybar,
// where the panel docks at the bottom of the viewport; a gallery tile sets both fields so the panel
// can target that image and float above its comment button instead.
interface ICommentAnchor {
  imageId: string;
  selector: string;
}

// One object rather than the comments' split open-state/anchor, so `imageId` never has to fall back
// to `curGalleryImage`, which goes stale once the lightbox closes.
interface IReactionPicker extends IOpenState {
  imageId: string;
  selector: string;
  docked: boolean;
}

export type SelectionEditSignInReason = 'wrongAccount' | 'signIn';

// Edit on someone else's selection asks for a sign-in first; `requestedBy` is the customer id at
// that moment, so a later id change means the sign-in went through.
export interface IPendingSelectionEdit {
  selectionId: string;
  reason: SelectionEditSignInReason;
  requestedBy: string;
}

interface INewGalleryUiState {
  extendModeEnabled: boolean;
  mobileMenuExtended: boolean;
  mobileMenuView: MobileMenuView;
  mobileMenuStandalone: boolean;
  previousAnchor: string;
  downloadPinModal: IDownloadPinModal;
  downloadModal: IDownloadModal;
  photographerModal: IOpenState;
  shareCopyModal: IOpenState;
  sendSelectionModal: ISelectionModal;
  singleImageDownloadModal: IOpenState;
  discardSelectionModal: IOpenState;
  selectionLimitModal: IOpenState;
  saveSelectionModal: IOpenState;
  saveSelectionSheet: IOpenState;
  confirmFilterByFacesModal: IOpenState;
  commentIdentityModal: IOpenState;
  deleteCommentModal: IDeleteCommentModal;
  commentAnchor: ICommentAnchor | null;
  reactionPicker: IReactionPicker;
  pendingSelectionEdit: IPendingSelectionEdit | null;
}

const closed = (): IOpenState => ({ isOpen: false });

const initialState: INewGalleryUiState = {
  extendModeEnabled: false,
  mobileMenuExtended: false,
  mobileMenuView: 'main',
  mobileMenuStandalone: false,
  previousAnchor: '',
  downloadPinModal: closed(),
  downloadModal: { isOpen: false, scope: 'default' },
  photographerModal: closed(),
  shareCopyModal: closed(),
  sendSelectionModal: closed(),
  singleImageDownloadModal: closed(),
  discardSelectionModal: closed(),
  selectionLimitModal: closed(),
  saveSelectionModal: closed(),
  saveSelectionSheet: closed(),
  confirmFilterByFacesModal: closed(),
  commentIdentityModal: closed(),
  deleteCommentModal: { isOpen: false, commentId: null, imageId: null },
  commentAnchor: null,
  reactionPicker: { isOpen: false, imageId: '', selector: '', docked: true },
  pendingSelectionEdit: null
};

type ModalKey = {
  [Key in keyof INewGalleryUiState]: INewGalleryUiState[Key] extends IOpenState ? Key : never;
}[keyof INewGalleryUiState];

type SharedModalKey = 'shareModal';

const closeModals = (state: INewGalleryUiState, except?: ModalKey | SharedModalKey) => {
  (Object.keys(state) as (keyof INewGalleryUiState)[]).forEach((key) => {
    const value = state[key];

    if (key !== except && value && typeof value === 'object' && 'isOpen' in value) {
      value.isOpen = false;
    }
  });
};

const newGalleryUiSlice = createSlice({
  name: 'scrAppBookNew/ui',
  initialState,
  reducers: {
    resetNewGalleryUi: () => initialState,
    enableExtendMode(state) {
      state.extendModeEnabled = true;
    },
    disableExtendMode(state) {
      state.extendModeEnabled = false;
    },
    toggleMobileMenu(state) {
      state.mobileMenuExtended = !state.mobileMenuExtended;
      state.mobileMenuView = 'main';
      state.mobileMenuStandalone = false;
    },
    openMobileMenuView(state, { payload }: PayloadAction<MobileMenuView>) {
      state.mobileMenuExtended = true;
      state.mobileMenuView = payload;
      state.mobileMenuStandalone = payload !== 'main';
    },
    closeMobileMenu(state) {
      state.mobileMenuExtended = false;
      state.mobileMenuStandalone = false;
    },
    setMobileMenuView(state, { payload }: PayloadAction<MobileMenuView>) {
      state.mobileMenuView = payload;
    },
    savePreviousAnchor(state, { payload }: PayloadAction<string>) {
      state.previousAnchor = payload;
    },
    prepareNewDesignModal(state, { payload }: PayloadAction<ModalKey | SharedModalKey>) {
      closeModals(state, payload);
    },
    closeNewDesignModals(state) {
      closeModals(state);
    },
    toggleDownloadPinModal(
      state,
      { payload }: PayloadAction<Omit<IDownloadPinModal, 'isOpen'> | undefined>
    ) {
      state.downloadPinModal.isOpen = !state.downloadPinModal.isOpen;
      if (payload?.action) state.downloadPinModal.action = payload.action;
      if (payload?.url) state.downloadPinModal.url = payload.url;
      if (payload?.next) state.downloadPinModal.next = payload.next;
    },
    toggleDownloadModal(state, { payload }: PayloadAction<IDownloadModal['scope'] | undefined>) {
      state.downloadModal.isOpen = !state.downloadModal.isOpen;
      if (payload) state.downloadModal.scope = payload;
      if (!state.downloadModal.isOpen) state.downloadModal.scope = 'default';
    },
    // For results that land later: the customer may have closed or reopened the window meanwhile.
    openDownloadModal(state, { payload }: PayloadAction<IDownloadModal['scope'] | undefined>) {
      state.downloadModal.isOpen = true;
      if (payload) state.downloadModal.scope = payload;
    },
    closeDownloadModal(state) {
      state.downloadModal.isOpen = false;
      state.downloadModal.scope = 'default';
    },
    togglePhotographerModal(state) {
      state.photographerModal.isOpen = !state.photographerModal.isOpen;
    },
    closePhotographerModal(state) {
      state.photographerModal.isOpen = false;
    },
    openShareCopyModal(state) {
      state.shareCopyModal.isOpen = true;
    },
    closeShareCopyModal(state) {
      state.shareCopyModal.isOpen = false;
    },
    toggleSendSelectionModal(state, { payload }: PayloadAction<ISelection | undefined>) {
      state.sendSelectionModal.isOpen = !state.sendSelectionModal.isOpen;
      if (payload) {
        state.sendSelectionModal.selectionId = payload._id;
        state.sendSelectionModal.selectionName = payload.name;
      }
    },
    toggleSaveSelectionModal(state) {
      state.saveSelectionModal.isOpen = !state.saveSelectionModal.isOpen;
    },
    toggleSaveSelectionSheet(state) {
      state.saveSelectionSheet.isOpen = !state.saveSelectionSheet.isOpen;
    },
    toggleDiscardSelectionModal(state) {
      state.discardSelectionModal.isOpen = !state.discardSelectionModal.isOpen;
    },
    openSelectionLimitModal(state) {
      state.selectionLimitModal.isOpen = true;
    },
    closeSelectionLimitModal(state) {
      state.selectionLimitModal.isOpen = false;
    },
    openSingleImageDownload(state) {
      state.singleImageDownloadModal.isOpen = true;
    },
    closeSingleImageDownload(state) {
      state.singleImageDownloadModal.isOpen = false;
    },
    setConfirmFilterByFacesModal(state, { payload }: PayloadAction<boolean>) {
      state.confirmFilterByFacesModal.isOpen = payload;
    },
    closeSelectionSaveUi(state) {
      state.sendSelectionModal.isOpen = false;
      state.saveSelectionModal.isOpen = false;
      state.saveSelectionSheet.isOpen = false;
    },
    openCommentIdentityModal(state) {
      state.commentIdentityModal.isOpen = true;
    },
    closeCommentIdentityModal(state) {
      state.commentIdentityModal.isOpen = false;
    },
    openDeleteCommentModal(
      state,
      { payload }: PayloadAction<{ commentId: string; imageId: string }>
    ) {
      state.deleteCommentModal = { isOpen: true, ...payload };
    },
    closeDeleteCommentModal(state) {
      state.deleteCommentModal.isOpen = false;
    },
    openReactionPicker(state, { payload }: PayloadAction<Omit<IReactionPicker, 'isOpen'>>) {
      state.reactionPicker = { isOpen: true, ...payload };
    },
    closeReactionPicker(state) {
      state.reactionPicker.isOpen = false;
    },
    setCommentAnchor(state, { payload }: PayloadAction<ICommentAnchor>) {
      state.commentAnchor = payload;
    },
    clearCommentAnchor(state) {
      state.commentAnchor = null;
    },
    requestSelectionEditSignIn(state, { payload }: PayloadAction<IPendingSelectionEdit>) {
      state.pendingSelectionEdit = payload;
    },
    clearPendingSelectionEdit(state) {
      state.pendingSelectionEdit = null;
    }
  }
});

export const {
  resetNewGalleryUi,
  enableExtendMode,
  disableExtendMode,
  toggleMobileMenu,
  openMobileMenuView,
  closeMobileMenu,
  setMobileMenuView,
  savePreviousAnchor,
  prepareNewDesignModal,
  closeNewDesignModals,
  toggleDownloadPinModal,
  toggleDownloadModal,
  openDownloadModal,
  closeDownloadModal,
  togglePhotographerModal,
  closePhotographerModal,
  openShareCopyModal,
  closeShareCopyModal,
  toggleSendSelectionModal,
  toggleSaveSelectionModal,
  toggleSaveSelectionSheet,
  toggleDiscardSelectionModal,
  openSelectionLimitModal,
  closeSelectionLimitModal,
  openSingleImageDownload,
  closeSingleImageDownload,
  setConfirmFilterByFacesModal,
  closeSelectionSaveUi,
  openCommentIdentityModal,
  closeCommentIdentityModal,
  openDeleteCommentModal,
  closeDeleteCommentModal,
  setCommentAnchor,
  clearCommentAnchor,
  openReactionPicker,
  closeReactionPicker,
  requestSelectionEditSignIn,
  clearPendingSelectionEdit
} = newGalleryUiSlice.actions;

export const newGalleryUiReducer = newGalleryUiSlice.reducer;
